import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './hero.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useProducts } from '../../hooks/useProducts';
import {
  getProductImageUrl,
  getProductName,
  getProductId,
  calculateDiscount,
  getProductPrices,
} from '../../utils/helpers';
import HeroSlide from './HeroSlide';
import heropic from '../Assets/heropic.jpg';

const MAX_SLIDES = 8;
const AUTO_PLAY_MS = 8000;

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const normalizeProducts = (productsData) => {
  if (!productsData) return [];
  if (Array.isArray(productsData)) return productsData;
  if (Array.isArray(productsData?.products)) return productsData.products;
  if (Array.isArray(productsData?.data)) return productsData.data;
  if (Array.isArray(productsData?.data?.products)) return productsData.data.products;
  return [];
};

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useTranslation(['hero', 'common']);

  const { data: productsData, isLoading } = useProducts({ limit: 100 });

  const carouselSlides = useMemo(() => {
    const allProducts = normalizeProducts(productsData);
    if (allProducts.length === 0) return [];

    const flash = allProducts.filter((p) => p.isFlash);
    const rest = allProducts.filter((p) => !p.isFlash);
    const ordered = [...shuffleArray(flash), ...shuffleArray(rest)].slice(0, MAX_SLIDES);

    return ordered.map((product) => {
      const category = product.category || product.categoryData || {};
      const productName = product.name || getProductName(product) || 'Special Offer';
      const isFlash = Boolean(product.isFlash);
      const { price, comparePrice } = getProductPrices(product);
      const discount =
        comparePrice && comparePrice > price ? calculateDiscount(comparePrice, price) : 0;

      return {
        id: getProductId(product),
        image: getProductImageUrl(product),
        title: productName,
        subtitle:
          isFlash && discount > 0
            ? `${t('save')} ${discount}% - ${t('limitedTime')}`
            : discount > 0
              ? `${t('save')} ${discount}%`
              : t('featuredProduct'),
        link: `/product/${getProductId(product)}`,
        buttonText: isFlash ? t('shopFlashSale') : t('shopNow', { ns: 'common' }),
        isFlash,
        price,
        comparePrice,
        discount,
        categoryName: category.name || category,
      };
    });
  }, [productsData, t]);

  const fallbackSlide = useMemo(
    () => ({
      id: 'fallback',
      image: heropic,
      title: t('luxuryCollection'),
      subtitle: t('discoverPremium'),
      link: '/search',
      buttonText: t('shopNow', { ns: 'common' }),
      isFlash: false,
      price: null,
      comparePrice: null,
      discount: 0,
      categoryName: null,
    }),
    [t]
  );

  const slides = useMemo(() => {
    if (carouselSlides.length > 0) return carouselSlides.slice(0, MAX_SLIDES);
    return [fallbackSlide];
  }, [carouselSlides, fallbackSlide]);
  const effectiveSlideCount = slides.length;
  const showControls = slides.length > 1;

  useEffect(() => {
    setCurrentSlide(0);
  }, [effectiveSlideCount]);

  useEffect(() => {
    if (effectiveSlideCount <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % effectiveSlideCount);
    }, AUTO_PLAY_MS);
    return () => clearInterval(interval);
  }, [effectiveSlideCount]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    if (effectiveSlideCount === 0) return;
    setCurrentSlide((prev) => (prev + 1) % effectiveSlideCount);
  }, [effectiveSlideCount]);

  const prevSlide = useCallback(() => {
    if (effectiveSlideCount === 0) return;
    setCurrentSlide((prev) => (prev - 1 + effectiveSlideCount) % effectiveSlideCount);
  }, [effectiveSlideCount]);

  return (
    <section className="hero" aria-label={t('heroBanner', { defaultValue: 'Featured offers' })}>
      <div className="hero__container">
        <div className="hero__carousel">
          {isLoading && slides.length <= 1 ? (
            <div className="hero__skeleton" aria-busy="true" aria-label={t('loading', { ns: 'common' })} />
          ) : (
            slides.map((slide, index) => (
              <HeroSlide key={slide.id} slide={slide} isActive={index === currentSlide} />
            ))
          )}

          {showControls && (
            <>
              <button
                type="button"
                className="hero__nav hero__nav--prev"
                onClick={prevSlide}
                aria-label={t('previousSlide', { defaultValue: 'Previous slide' })}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                className="hero__nav hero__nav--next"
                onClick={nextSlide}
                aria-label={t('nextSlide', { defaultValue: 'Next slide' })}
              >
                <ChevronRight size={24} />
              </button>
              <div className="hero__dots" role="tablist" aria-label={t('slideNavigation', { defaultValue: 'Slides' })}>
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    role="tab"
                    aria-selected={index === currentSlide}
                    aria-label={`${t('slide', { defaultValue: 'Slide' })} ${index + 1}`}
                    className={`hero__dot ${index === currentSlide ? 'hero__dot--active' : ''}`}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
